import { Box, Button, Column, Row, TextField, Typography } from '@tempmee/react';
import { OutlineEyeIcon, OutlineEyeOffIcon } from '@tempmee/react-icons';
import { colors } from '@tempmee/tailwind';
import { useEffect, useState } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router';
import { InvalidCredentialsError, useSignIn } from '~/domain/Auth';
import { removeWhitespace } from '~/utils/String';
import {
  activateOnSpaceOrEnter,
  AuthErrorBanner,
  EMAIL_REGEX,
  LINK_STYLE,
  useAuthFormSubmit,
  useTurnstileToken,
} from '../shared';

const INVALID_CREDENTIALS_MESSAGE = 'Your email and password combination does not match.';

export interface ILoginFormValues {
  email: string;
  password: string;
}

interface ILoginLocationState {
  prefillEmail?: string;
}

export const LoginForm = () => {
  const location = useLocation();
  const prefillEmail = (location.state as ILoginLocationState | null)?.prefillEmail ?? '';

  const methods = useForm<ILoginFormValues>({
    defaultValues: { email: prefillEmail, password: '' },
    mode: 'onSubmit',
  });

  return (
    <FormProvider {...methods}>
      <LoginFormInner />
    </FormProvider>
  );
};

const LoginFormInner = () => {
  const navigate = useNavigate();
  const { mutateAsync: signIn, isPending, isError } = useSignIn();
  const {
    token: turnstileToken,
    widget: turnstileWidget,
    reset: resetTurnstile,
    enabled: turnstileEnabled,
  } = useTurnstileToken();

  const { submit, bannerVisible, bannerRef, serverMessage } = useAuthFormSubmit<ILoginFormValues>({
    onValid: async (values) => {
      try {
        await signIn({ ...values, turnstileToken });
      } catch (err) {
        // Surface wrong email/password inline (banner + red fields). Every
        // other error is already shown via the global pop-up, so swallow it
        // here to avoid a duplicate inline banner.
        if (err instanceof InvalidCredentialsError) throw err;
      }
    },
  });

  useEffect(() => {
    if (isError) resetTurnstile();
  }, [isError, resetTurnstile]);

  const {
    register,
    formState: { errors },
  } = useFormContext<ILoginFormValues>();

  const [showPassword, setShowPassword] = useState(false);

  const goToForgotPassword = () => navigate('/forgot-password');
  const goToSignup = () => navigate('/signup');

  const emailRegister = register('email', {
    required: 'Email is required',
    pattern: { value: EMAIL_REGEX, message: 'Enter a valid email' },
  });

  // A wrong email/password combo is a credentials error: the BE can't tell
  // which field is wrong, so both are highlighted while the banner is shown.
  const hasCredentialsError = Boolean(serverMessage);
  const emailHasError = Boolean(errors.email) || hasCredentialsError;
  const passwordHasError = Boolean(errors.password) || hasCredentialsError;

  return (
    <form onSubmit={submit} data-testid="login-form" noValidate>
      <Column pt={6} gap={8}>
        <Column gap={4}>
          <Typography bold size="heading_3">
            Welcome Back, Offices!
          </Typography>
          <Typography variant="body1" color={colors.neutral[500]}>
            Log in to connect with trusted dental professionals and keep your schedule covered.
          </Typography>
        </Column>

        {/* Wrapper keeps a stable scrollIntoView target when the banner is hidden. */}
        <Box ref={bannerRef}>
          <AuthErrorBanner
            visible={bannerVisible}
            message={hasCredentialsError ? INVALID_CREDENTIALS_MESSAGE : undefined}
            prefix={hasCredentialsError ? '' : undefined}
          />
          <Column gap={8} pt={bannerVisible ? 3 : 0}>
            <TextField
              label="Email"
              data-cy="input-email"
              placeholder="Enter your email"
              error={emailHasError}
              aria-invalid={emailHasError || undefined}
              {...emailRegister}
              onChange={(e) => {
                const stripped = removeWhitespace(e.target.value);
                if (stripped !== e.target.value) e.target.value = stripped;
                emailRegister.onChange(e);
              }}
            />
            <TextField
              label="Password"
              type={showPassword ? 'text' : 'password'}
              data-cy="input-password"
              placeholder="Enter your password"
              error={passwordHasError}
              aria-invalid={passwordHasError || undefined}
              trailingIcon={
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                >
                  {showPassword ? <OutlineEyeOffIcon /> : <OutlineEyeIcon />}
                </button>
              }
              {...register('password', { required: 'Password is required' })}
            />
          </Column>
        </Box>

        <Row justify="center">
          <Typography
            as="span"
            bold
            variant="body1"
            color={colors.secondary[500]}
            style={LINK_STYLE}
            role="button"
            tabIndex={0}
            data-cy="forgot-password-link"
            onClick={goToForgotPassword}
            onKeyDown={activateOnSpaceOrEnter(goToForgotPassword)}
          >
            Forgot Password?
          </Typography>
        </Row>

        {turnstileWidget}

        <Row justify="center">
          <Button
            data-cy="btn-log-in"
            type="submit"
            variant="primary"
            size="lg"
            isLoading={isPending}
            disabled={turnstileEnabled && !turnstileToken}
          >
            Sign in
          </Button>
        </Row>

        <Row justify="center" align="center" wrap="nowrap" gap={1}>
          <Typography variant="body1" color={colors.neutral[500]}>
            Don&apos;t have an account?
          </Typography>
          <Typography
            as="span"
            bold
            variant="body1"
            color={colors.secondary[500]}
            style={LINK_STYLE}
            role="button"
            tabIndex={0}
            data-cy="link-sign-up"
            onClick={goToSignup}
            onKeyDown={activateOnSpaceOrEnter(goToSignup)}
          >
            Register Now
          </Typography>
        </Row>
      </Column>
    </form>
  );
};
